"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Button, Modal, useToast } from "@/components/ui";
import {
  PUSH_ONBOARDING_DONE_KEY,
  isStandaloneDisplayMode,
  shouldShowPushOnboarding,
} from "@/lib/push";
import { usePushSubscription } from "@/lib/use-push-subscription";

/**
 * First-launch push opt-in modal for the installed PWA. Browsers only allow
 * the native permission dialog from a user gesture, so "ask automatically
 * after install" means: show this modal on the first standalone launch and
 * let its button trigger the real request. Any answer (enable, later, close)
 * settles it forever via localStorage; the modal also never returns once the
 * permission itself is granted or denied.
 *
 * The outer component gates on client-only environment (standalone + the
 * localStorage flag) before mounting the inner modal, so the push status
 * fetch only happens inside an installed app that still needs to ask.
 */
export function PushOnboarding() {
  // False until the environment is known — client-only knowledge, resolved
  // after mount (same pattern as AgeGate / PushPrompt).
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEligible(
      isStandaloneDisplayMode() &&
        window.localStorage.getItem(PUSH_ONBOARDING_DONE_KEY) === null,
    );
  }, []);

  if (!eligible) return null;
  return <PushOnboardingModal onDone={() => setEligible(false)} />;
}

function PushOnboardingModal({ onDone }: { onDone: () => void }) {
  const t = useTranslations("push");
  const { toast } = useToast();
  const push = usePushSubscription();

  const open =
    push.loaded &&
    shouldShowPushOnboarding({
      standalone: true, // the parent only mounts this inside the installed app
      supported: push.supported,
      vapidPublicKey: push.vapidPublicKey,
      permission: push.permission,
      done: false, // ditto — the parent checked the localStorage flag
    });

  function settle() {
    window.localStorage.setItem(PUSH_ONBOARDING_DONE_KEY, "1");
    onDone();
  }

  async function enable() {
    // "subscribed" and "denied" both settle the question; only a hard
    // failure keeps the modal open (with a toast) so the user can retry.
    if ((await push.subscribe()) === "error") {
      toast({ title: t("errorTitle"), variant: "danger" });
      return;
    }
    settle();
  }

  return (
    <Modal
      open={open}
      onClose={settle}
      title={t("onboarding.title")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={settle} disabled={push.busy}>
            {t("onboarding.later")}
          </Button>
          <Button onClick={enable} isLoading={push.busy}>
            {t("onboarding.enable")}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-white/65">{t("onboarding.body")}</p>
    </Modal>
  );
}
